<template>
	<div>
		<users-submenu />

		<core-layout-content full>
			<!-- Headline -->
			<core-layout-headline-and-actions>
				<template slot="headline">
					{{ $t("permission_groups") }}
				</template>
				<template slot="actions">
					<j-button
						v-can:and="['writePermissionGroups']"
						@click="$modal.show('createGroup')"
					>
						<crm-icon name="add" size="small" color="c5" />
						{{ $t("create_permission_group") }}
					</j-button>
				</template>
			</core-layout-headline-and-actions>

			<!-- Data Container -->
			<j-card class="bg5">
				<j-data-table
					:header="header"
					:list="computedList"
					:sortable="false"
					:isLoading="isLoading"
					@sort="onSort($event)"
				>
					<template slot="list" slot-scope="{ item }">
						<td v-for="(column, i) in header" :key="i">
							<template>
								<!-- Name -->
								<template v-if="column.field === 'name'">
									<router-link :to="`update/${item.id}`" :append="true">{{
										item.name
									}}</router-link>
								</template>

								<!-- Others -->
								<template v-else>
									{{ item[column.field] }}
								</template>

								<!-- Actions Field -->
								<template v-if="column.field === 'actions'">
									<div class="action">
										<!-- Delete -->
										<crm-icon
											name="delete"
											size="normal"
											color="cError"
											@click.native="showModal(item.id)"
											v-can="'writePermissionGroups'"
										/>
									</div>
								</template>
							</template>
						</td>
					</template>
					<template slot="empty">
						{{ $t("errors.no_data") }}
					</template>
				</j-data-table>
			</j-card>
		</core-layout-content>

		<!-- Create group in modal -->
		<v-modal
			name="createGroup"
			height="auto"
			:scrollable="true"
			:adaptive="true"
		>
			<!-- Component that will create a permission group -->
			<permission-groups-create-group
				@created="
					readAll()
					$modal.hide('createGroup')
				"
			/>
		</v-modal>

		<!-- Delete confirmation modal -->
		<j-dialog
			:height="200"
			:width="600"
			:title="$t('delete')"
			type="delete"
			@confirm="deletePermissionGroup(selectedItemId)"
			>{{ $t("errors.are_you_sure_you_want_to_delete_permission_group") }}</j-dialog
		>
	</div>
</template>

<script>
import CrmIcon from "@/components/CrmIcon/"
import JButton from "@/components/JButton/"
import JCard from "@/components/JCard/"
import JDataTable from "@/components/JDataTable/"
import JSearch from "@/components/JSearch/"
import JPagination from "@/components/JPagination/"
import JDialog from "@/components/JDialog/"
import JBaseInputSearch from "@/components/JBaseInputSearch/"
import PermissionGroupsCreateGroup from "../components/PermissionGroupsCreateGroup"
import CoreLayoutContent from "@/modules/core/components/CoreLayoutContent"
import CoreLayoutHeadlineAndActions from "@/modules/core/components/CoreLayoutHeadlineAndActions"
import UsersSubmenu from "../components/UsersSubmenu"
import { mapState } from "vuex"
import dayjs from "dayjs"
import api from "@/modules/users/api/"
import { gql } from "@/api/utils.js"
import crmConfig from "@/config/"

export default {
	components: {
		CrmIcon,
		JButton,
		JCard,
		JDialog,
		JDataTable,
		JSearch,
		JPagination,
		JBaseInputSearch,
		PermissionGroupsCreateGroup,
		CoreLayoutContent,
		CoreLayoutHeadlineAndActions,
		UsersSubmenu
	},

	data() {
		return {
			isLoading: false,

			// Actual fetched data
			listing: {
				list: []
			},

			// Header of table
			header: [
				{
					field: "name",
					text: this.$t("name")
				},
				{
					field: "description",
					text: this.$t("description")
				},
				{
					text: this.$t("created"),
					field: "createdAt"
				},
				{
					text: this.$t("actions"),
					field: "actions"
				}
			],
			selectedItemId: null
		}
	},

	computed: {
		...mapState(["settings"]),

		computedList() {
			return this.listing.list.map(item => ({
				...item,
				createdAt: item.createdAt
					? dayjs(item.createdAt).format(this.settings.dateTimeFormat)
					: null
			}))
		}
	},

	methods: {
		async readAll() {
			this.isLoading = true
			const res = await api.readAllPermissionGroups()
			this.isLoading = false

			if (res.errors) this.$store.dispatch("showError", res.errors)
			else this.listing.list = res.data.permissionGroups.reverse() // Reversing order - newest most top
		},
		showModal(id) {
			this.selectedItemId = id
			this.$modal.show("j-dialog")
		},
		hideModal(id) {
			this.selectedItemId = null
			this.$modal.hide("j-dialog")
		},
		async deletePermissionGroup(id) {
			const res = await api.deletePermissionGroup({ id })
			this.hideModal(null)

			if (res?.data?.deletePermissionGroup?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.errors ||
							res?.data?.deletePermissionGroup?.errors ||
							res?.data?.deletePermissionGroup?.errors_2?.message ||
							"Oops ! An error occured."
					}
				})
			} else
				this.$store.dispatch("addMessage", {
					message: {
						type: "success",
						text: this.$t("permission_group_has_been_deleted_successfully")
					}
				})

			this.readAll()
		}
	},

	watch: {
		// Watch change of router and always refetch data
		$route: {
			handler() {
				// Only update if we are loading the main listing view
				// It's based on name of route
				// Names are defined in router
				// If we don't do it, it would refetch data for listing anytime we open modal
				if (this.$route.name === "UsersPermissionGroups") this.readAll()
			},
			immediate: true
		}
	}
}
</script>
